/**
 * OneDrive / SharePoint Picker Page
 *
 * This page is served on the central auth domain (e.g., mylogin.dev.kenbakke.com)
 * and handles the OneDrive / SharePoint file picker flow for multi-tenant environments.
 *
 * Uses a custom file picker UI built on Graph API instead of the OneDrive SDK
 * to avoid authentication complications in popup scenarios.
 *
 * Flow:
 * 1. Tenant subdomain opens this page in a popup with returnOrigin parameter
 * 2. User clicks "Open OneDrive" / "Open SharePoint" - redirects to Microsoft login
 * 3. After auth, redirects back here with token
 * 4. Shows custom file picker UI using Graph API
 *    - OneDrive mode: browse personal drive files
 *    - SharePoint mode: sites -> document libraries -> files
 * 5. Sends the result back to the tenant via postMessage
 * 6. Closes the popup
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { PublicClientApplication, AuthenticationResult } from "@azure/msal-browser";
import {
    Button,
    CircularProgress,
    Typography,
    Box,
    Alert,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemButton,
    Breadcrumbs,
    Link,
    Paper
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import HomeIcon from "@mui/icons-material/Home";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BusinessIcon from "@mui/icons-material/Business";

import { msalConfig } from "../../../common/utilities/constants/constants";
import {
    isValidOrigin,
    getMainDomain,
    sendMessageToOpener,
    GooglePickerMessage
} from "../../../common/utilities/google-picker/google-picker-message.util";

import "./onedrive-picker.scss";

interface OneDrivePickerResult {
    success: boolean;
    url?: string;
    accessToken?: string;
    email?: string;
    error?: string;
}

interface DriveItem {
    id: string;
    name: string;
    webUrl: string;
    folder?: { childCount: number };
    file?: { mimeType: string };
    size?: number;
    lastModifiedDateTime?: string;
}

interface SharePointSite {
    id: string;
    name: string;
    displayName: string;
    webUrl: string;
}

interface SharePointDrive {
    id: string;
    name: string;
    webUrl: string;
    driveType: string;
}

interface BreadcrumbItem {
    id: string;
    name: string;
}

type PickerMode = "onedrive" | "sharepoint";

// SharePoint navigation phases
type SharePointPhase = "sites" | "libraries" | "files";

type PickerStatus =
    | "initializing"
    | "ready"
    | "authenticating"
    | "picking"
    | "loading"
    | "success"
    | "error"
    | "invalid-origin";

// Session storage keys for persisting state across redirect
const STORAGE_KEY_RETURN_ORIGIN = "onedrive_picker_return_origin";
const STORAGE_KEY_USE_SDK8 = "onedrive_picker_use_sdk8";
const STORAGE_KEY_MODE = "onedrive_picker_mode";

const OneDrivePickerPage = () => {
    const [status, setStatus] = useState<PickerStatus>("initializing");
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [msalInstance] = useState(() => new PublicClientApplication(msalConfig));
    const [msalInitialized, setMsalInitialized] = useState(false);
    const [accessToken, setAccessToken] = useState<string>("");
    const [, setUserEmail] = useState<string>("");
    const authResultRef = useRef<AuthenticationResult | null>(null);

    // File picker state
    const [items, setItems] = useState<DriveItem[]>([]);
    const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
    const [selectedItem, setSelectedItem] = useState<DriveItem | null>(null);
    const [isLoadingItems, setIsLoadingItems] = useState(false);

    // SharePoint-specific state
    const [spPhase, setSpPhase] = useState<SharePointPhase>("sites");
    const [sites, setSites] = useState<SharePointSite[]>([]);
    const [drives, setDrives] = useState<SharePointDrive[]>([]);
    const [, setSelectedSiteId] = useState<string>("");
    const [activeDriveId, setActiveDriveId] = useState<string>("");

    // Get parameters from URL or sessionStorage (for after redirect)
    const { returnOrigin, pickerMode } = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        let origin = params.get("returnOrigin") || "";
        let mode = (params.get("mode") || "onedrive") as PickerMode;

        // If not in URL, try sessionStorage (we're returning from redirect)
        if (!origin) {
            origin = sessionStorage.getItem(STORAGE_KEY_RETURN_ORIGIN) || "";
        }
        const storedMode = sessionStorage.getItem(STORAGE_KEY_MODE);
        if (storedMode) {
            mode = storedMode as PickerMode;
        }

        // Store in sessionStorage for after redirect
        if (origin) {
            sessionStorage.setItem(STORAGE_KEY_RETURN_ORIGIN, origin);
            sessionStorage.setItem(STORAGE_KEY_USE_SDK8, "true");
            sessionStorage.setItem(STORAGE_KEY_MODE, mode);
        }

        return { returnOrigin: origin, pickerMode: mode };
    }, []);

    const isSharePoint = pickerMode === "sharepoint";

    // Initialize breadcrumbs based on mode
    useEffect(() => {
        setBreadcrumbs([{ id: "root", name: isSharePoint ? "SharePoint Sites" : "My Files" }]);
    }, [isSharePoint]);

    // Scopes differ by mode
    const msalScopes = useMemo(
        () =>
            isSharePoint
                ? ["Sites.Read.All", "Files.Read.All", "offline_access"]
                : ["Files.Read.All", "offline_access"],
        [isSharePoint]
    );

    // Validate the return origin
    const isOriginValid = useMemo(() => {
        if (!returnOrigin) return false;
        const mainDomain = getMainDomain();
        return isValidOrigin(returnOrigin, [mainDomain]);
    }, [returnOrigin]);

    // Send result to parent and close
    const sendResultAndClose = useCallback(
        (result: OneDrivePickerResult) => {
            // Clean up sessionStorage
            sessionStorage.removeItem(STORAGE_KEY_RETURN_ORIGIN);
            sessionStorage.removeItem(STORAGE_KEY_USE_SDK8);
            sessionStorage.removeItem(STORAGE_KEY_MODE);

            const message: GooglePickerMessage = {
                type: "GOOGLE_PICKER_RESULT",
                payload: {
                    success: result.success,
                    url: result.url,
                    accessToken: result.accessToken,
                    error: result.error
                },
                origin: window.location.origin
            };

            sendMessageToOpener(message, returnOrigin);

            setTimeout(() => {
                window.close();
            }, 100);
        },
        [returnOrigin]
    );

    // Send cancel to parent
    const sendCancelAndClose = useCallback(() => {
        // Clean up sessionStorage
        sessionStorage.removeItem(STORAGE_KEY_RETURN_ORIGIN);
        sessionStorage.removeItem(STORAGE_KEY_USE_SDK8);
        sessionStorage.removeItem(STORAGE_KEY_MODE);

        const message: GooglePickerMessage = {
            type: "GOOGLE_PICKER_CANCEL",
            origin: window.location.origin
        };

        sendMessageToOpener(message, returnOrigin);

        setTimeout(() => {
            window.close();
        }, 100);
    }, [returnOrigin]);

    // ---- SharePoint: Load sites ----
    const loadSites = useCallback(async () => {
        if (!accessToken) return;

        setIsLoadingItems(true);
        setErrorMessage("");
        try {
            const response = await fetch("https://graph.microsoft.com/v1.0/sites?search=*", {
                headers: { Authorization: `Bearer ${accessToken}` }
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error("OneDrivePickerPage: Sites API error:", errorText);
                throw new Error(`Failed to load sites: ${response.status}`);
            }

            const data = await response.json();

            const sortedSites = (data.value || []).sort((a: SharePointSite, b: SharePointSite) =>
                (a.displayName || a.name).localeCompare(b.displayName || b.name)
            );

            setSites(sortedSites);
            setSelectedItem(null);
        } catch (error) {
            console.error("OneDrivePickerPage: Error loading sites", error);
            setErrorMessage("Failed to load SharePoint sites. Please try again.");
            setStatus("error");
        } finally {
            setIsLoadingItems(false);
        }
    }, [accessToken]);

    // ---- SharePoint: Load document libraries for a site ----
    const loadDrives = useCallback(
        async (siteId: string) => {
            if (!accessToken) return;

            setIsLoadingItems(true);
            setErrorMessage("");
            try {
                const response = await fetch(
                    `https://graph.microsoft.com/v1.0/sites/${siteId}/drives`,
                    { headers: { Authorization: `Bearer ${accessToken}` } }
                );

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error("OneDrivePickerPage: Drives API error:", errorText);
                    throw new Error(`Failed to load document libraries: ${response.status}`);
                }

                const data = await response.json();

                const sortedDrives = (data.value || []).sort(
                    (a: SharePointDrive, b: SharePointDrive) => a.name.localeCompare(b.name)
                );

                setDrives(sortedDrives);
                setSelectedItem(null);
            } catch (error) {
                console.error("OneDrivePickerPage: Error loading drives", error);
                setErrorMessage("Failed to load document libraries. Please try again.");
                setStatus("error");
            } finally {
                setIsLoadingItems(false);
            }
        },
        [accessToken]
    );

    // ---- SharePoint: Load items from a drive/folder ----
    const loadDriveItems = useCallback(
        async (driveId: string, folderId: string) => {
            if (!accessToken) return;

            setIsLoadingItems(true);
            setErrorMessage("");
            try {
                const endpoint =
                    folderId === "root"
                        ? `https://graph.microsoft.com/v1.0/drives/${driveId}/root/children`
                        : `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${folderId}/children`;

                const response = await fetch(endpoint, {
                    headers: { Authorization: `Bearer ${accessToken}` }
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error("OneDrivePickerPage: Drive items API error:", errorText);
                    throw new Error(`Failed to load files: ${response.status}`);
                }

                const data = await response.json();

                const sortedItems = (data.value || []).sort((a: DriveItem, b: DriveItem) => {
                    if (a.folder && !b.folder) return -1;
                    if (!a.folder && b.folder) return 1;
                    return a.name.localeCompare(b.name);
                });

                setItems(sortedItems);
                setSelectedItem(null);
            } catch (error) {
                console.error("OneDrivePickerPage: Error loading drive items", error);
                setErrorMessage("Failed to load files. Please try again.");
                setStatus("error");
            } finally {
                setIsLoadingItems(false);
            }
        },
        [accessToken]
    );

    // ---- OneDrive: Load items from a folder using Graph API ----
    const loadItems = useCallback(
        async (folderId: string) => {
            if (!accessToken) return;

            setIsLoadingItems(true);
            setErrorMessage("");
            try {
                const endpoint =
                    folderId === "root"
                        ? "https://graph.microsoft.com/v1.0/me/drive/root/children"
                        : `https://graph.microsoft.com/v1.0/me/drive/items/${folderId}/children`;

                const response = await fetch(endpoint, {
                    headers: { Authorization: `Bearer ${accessToken}` }
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error("OneDrivePickerPage: API error response:", errorText);
                    throw new Error(`Failed to load files: ${response.status}`);
                }

                const data = await response.json();

                const sortedItems = (data.value || []).sort((a: DriveItem, b: DriveItem) => {
                    if (a.folder && !b.folder) return -1;
                    if (!a.folder && b.folder) return 1;
                    return a.name.localeCompare(b.name);
                });

                setItems(sortedItems);
                setSelectedItem(null);
            } catch (error) {
                console.error("OneDrivePickerPage: Error loading items", error);
                setErrorMessage("Failed to load files. Please try again.");
                setStatus("error");
            } finally {
                setIsLoadingItems(false);
            }
        },
        [accessToken]
    );

    // ---- SharePoint: Navigate to a site ----
    const navigateToSite = useCallback(
        (site: SharePointSite) => {
            setSelectedSiteId(site.id);
            setSpPhase("libraries");
            setBreadcrumbs([
                { id: "root", name: "SharePoint Sites" },
                { id: site.id, name: site.displayName || site.name }
            ]);
            loadDrives(site.id);
        },
        [loadDrives]
    );

    // ---- SharePoint: Navigate into a document library ----
    const navigateToLibrary = useCallback(
        (drive: SharePointDrive) => {
            setActiveDriveId(drive.id);
            setSpPhase("files");
            setBreadcrumbs(prev => [...prev, { id: drive.id, name: drive.name }]);
            loadDriveItems(drive.id, "root");
        },
        [loadDriveItems]
    );

    // Navigate to a folder (OneDrive mode or SharePoint files phase)
    const navigateToFolder = useCallback(
        (item: DriveItem) => {
            setBreadcrumbs(prev => [...prev, { id: item.id, name: item.name }]);
            if (isSharePoint) {
                loadDriveItems(activeDriveId, item.id);
            } else {
                loadItems(item.id);
            }
        },
        [isSharePoint, activeDriveId, loadDriveItems, loadItems]
    );

    // Navigate using breadcrumb
    const navigateToBreadcrumb = useCallback(
        (index: number) => {
            const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
            setBreadcrumbs(newBreadcrumbs);

            if (isSharePoint) {
                if (index === 0) {
                    // Back to sites list
                    setSpPhase("sites");
                    setSelectedSiteId("");
                    setActiveDriveId("");
                    setItems([]);
                    setDrives([]);
                    loadSites();
                } else if (index === 1) {
                    // Back to libraries list for the selected site
                    const siteId = newBreadcrumbs[1].id;
                    setSpPhase("libraries");
                    setSelectedSiteId(siteId);
                    setActiveDriveId("");
                    setItems([]);
                    loadDrives(siteId);
                } else if (index === 2) {
                    // Back to root of a library
                    const driveId = newBreadcrumbs[2].id;
                    setSpPhase("files");
                    setActiveDriveId(driveId);
                    loadDriveItems(driveId, "root");
                } else {
                    // Navigating within files - the last breadcrumb is the folder
                    const folderId = newBreadcrumbs[newBreadcrumbs.length - 1].id;
                    loadDriveItems(activeDriveId, folderId);
                }
            } else {
                loadItems(newBreadcrumbs[newBreadcrumbs.length - 1].id);
            }
        },
        [breadcrumbs, isSharePoint, activeDriveId, loadSites, loadDrives, loadDriveItems, loadItems]
    );

    // Go back one level
    const goBack = useCallback(() => {
        if (breadcrumbs.length > 1) {
            navigateToBreadcrumb(breadcrumbs.length - 2);
        }
    }, [breadcrumbs.length, navigateToBreadcrumb]);

    // Handle item click - selects the item (file or folder)
    const handleItemClick = useCallback((item: DriveItem) => {
        setSelectedItem(item);
    }, []);

    // Handle item double click - navigate into folder, or select file
    const handleItemDoubleClick = useCallback(
        (item: DriveItem) => {
            if (item.folder) {
                navigateToFolder(item);
            } else {
                setSelectedItem(item);
            }
        },
        [navigateToFolder]
    );

    // Handle select button click
    const handleSelect = useCallback(() => {
        if (selectedItem) {
            setStatus("success");
            sendResultAndClose({
                success: true,
                url: selectedItem.webUrl,
                accessToken: accessToken
            });
        }
    }, [selectedItem, accessToken, sendResultAndClose]);

    // Initialize MSAL and handle redirect response
    useEffect(() => {
        const initMsal = async () => {
            try {
                await msalInstance.initialize();

                const response = await msalInstance.handleRedirectPromise();

                if (response) {
                    authResultRef.current = response;

                    const tokenResponse = await msalInstance.acquireTokenSilent({
                        scopes: msalScopes,
                        account: response.account
                    });

                    setAccessToken(tokenResponse.accessToken);
                    setUserEmail(response.account.username);
                    setMsalInitialized(true);

                    setStatus("picking");
                } else {
                    setMsalInitialized(true);
                }
            } catch (error) {
                console.error("OneDrivePickerPage: MSAL initialization error", error);
                setErrorMessage("Failed to initialize Microsoft authentication.");
                setStatus("error");
            }
        };

        if (isOriginValid) {
            initMsal();
        }
    }, [msalInstance, isOriginValid, msalScopes]);

    // Load initial data when entering picking state
    useEffect(() => {
        if (status === "picking" && accessToken) {
            if (isSharePoint) {
                if (sites.length === 0 && spPhase === "sites") {
                    loadSites();
                }
            } else {
                if (items.length === 0) {
                    loadItems("root");
                }
            }
        }
    }, [
        status,
        accessToken,
        isSharePoint,
        sites.length,
        spPhase,
        items.length,
        loadSites,
        loadItems
    ]);

    // Initialize and validate on mount
    useEffect(() => {
        if (!returnOrigin) {
            setStatus("invalid-origin");
            setErrorMessage("Missing return origin parameter");
            return;
        }

        if (!isOriginValid) {
            setStatus("invalid-origin");
            setErrorMessage(
                "Invalid origin. This picker can only be opened from authorized domains."
            );
            return;
        }

        if (!msalConfig.auth.clientId) {
            setStatus("error");
            setErrorMessage("Microsoft authentication is not configured.");
            return;
        }
    }, [returnOrigin, isOriginValid]);

    // Set ready once MSAL is initialized (only if not returning from redirect)
    useEffect(() => {
        if (
            msalInitialized &&
            isOriginValid &&
            status === "initializing" &&
            !authResultRef.current
        ) {
            setStatus("ready");
        }
    }, [msalInitialized, isOriginValid, status]);

    // Handle open picker button click - uses redirect flow
    const handleOpenPicker = useCallback(async () => {
        if (!msalInitialized) {
            setErrorMessage("Microsoft authentication is not ready. Please wait.");
            return;
        }

        setStatus("authenticating");

        try {
            await msalInstance.loginRedirect({
                scopes: msalScopes,
                redirectUri: window.location.origin + "/blank.html"
            });
        } catch (error: any) {
            console.error("OneDrivePickerPage: Authentication error", error);

            if (error.errorCode === "user_cancelled" || error.errorMessage?.includes("cancelled")) {
                setStatus("ready");
                return;
            }

            setErrorMessage("Microsoft authentication failed. Please try again.");
            setStatus("error");
        }
    }, [msalInitialized, msalInstance, msalScopes]);

    // Handle cancel
    const handleCancel = useCallback(() => {
        sendCancelAndClose();
    }, [sendCancelAndClose]);

    // Handle retry
    const handleRetry = useCallback(() => {
        setErrorMessage("");
        setStatus("ready");
    }, []);

    // Format file size
    const formatSize = (bytes?: number) => {
        if (!bytes) return "";
        const units = ["B", "KB", "MB", "GB"];
        let i = 0;
        let size = bytes;
        while (size >= 1024 && i < units.length - 1) {
            size /= 1024;
            i++;
        }
        return `${size.toFixed(1)} ${units[i]}`;
    };

    // ---- Render SharePoint sites list ----
    const renderSitesList = () => (
        <Paper variant="outlined" sx={{ maxHeight: 350, overflow: "auto", mb: 2 }}>
            {isLoadingItems ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                    <CircularProgress size={30} />
                </Box>
            ) : sites.length === 0 ? (
                <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography color="text.secondary">No SharePoint sites found</Typography>
                </Box>
            ) : (
                <List dense>
                    {sites.map(site => (
                        <ListItem key={site.id} disablePadding>
                            <ListItemButton onDoubleClick={() => navigateToSite(site)}>
                                <ListItemIcon>
                                    <BusinessIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={site.displayName || site.name}
                                    secondary={site.webUrl}
                                />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            )}
        </Paper>
    );

    // ---- Render SharePoint document libraries list ----
    const renderDrivesList = () => (
        <Paper variant="outlined" sx={{ maxHeight: 350, overflow: "auto", mb: 2 }}>
            {isLoadingItems ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                    <CircularProgress size={30} />
                </Box>
            ) : drives.length === 0 ? (
                <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography color="text.secondary">No document libraries found</Typography>
                </Box>
            ) : (
                <List dense>
                    {drives.map(drive => (
                        <ListItem key={drive.id} disablePadding>
                            <ListItemButton onDoubleClick={() => navigateToLibrary(drive)}>
                                <ListItemIcon>
                                    <FolderIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText primary={drive.name} secondary={drive.driveType} />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            )}
        </Paper>
    );

    // ---- Render file/folder list (shared between OneDrive and SharePoint files phase) ----
    const renderFileList = () => (
        <Paper variant="outlined" sx={{ maxHeight: 350, overflow: "auto", mb: 2 }}>
            {isLoadingItems ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                    <CircularProgress size={30} />
                </Box>
            ) : items.length === 0 ? (
                <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography color="text.secondary">This folder is empty</Typography>
                </Box>
            ) : (
                <List dense>
                    {items.map(item => (
                        <ListItem key={item.id} disablePadding>
                            <ListItemButton
                                selected={selectedItem?.id === item.id}
                                onClick={() => handleItemClick(item)}
                                onDoubleClick={() => handleItemDoubleClick(item)}
                            >
                                <ListItemIcon>
                                    {item.folder ? (
                                        <FolderIcon color="primary" />
                                    ) : (
                                        <InsertDriveFileIcon color="action" />
                                    )}
                                </ListItemIcon>
                                <ListItemText
                                    primary={item.name}
                                    secondary={
                                        item.folder
                                            ? `${item.folder.childCount || 0} items`
                                            : formatSize(item.size)
                                    }
                                />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            )}
        </Paper>
    );

    // Render file picker UI
    const renderFilePicker = () => {
        const showingFiles = !isSharePoint || spPhase === "files";
        const emptyStateHint = isSharePoint
            ? spPhase === "sites"
                ? "Double-click a site to open it"
                : spPhase === "libraries"
                  ? "Double-click a library to browse its files"
                  : "Click to select, double-click folder to open"
            : "Click to select, double-click folder to open";

        return (
            <Box className="onedrive-picker-page__file-picker">
                {/* Breadcrumbs */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
                    <Button
                        size="small"
                        disabled={breadcrumbs.length <= 1}
                        onClick={goBack}
                        startIcon={<ArrowBackIcon />}
                    >
                        Back
                    </Button>
                    <Breadcrumbs sx={{ flex: 1 }}>
                        {breadcrumbs.map((crumb, index) => (
                            <Link
                                key={crumb.id}
                                component="button"
                                variant="body2"
                                onClick={() => navigateToBreadcrumb(index)}
                                sx={{
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center"
                                }}
                            >
                                {index === 0 && <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />}
                                {crumb.name}
                            </Link>
                        ))}
                    </Breadcrumbs>
                </Box>

                {/* Content area — depends on mode and phase */}
                {isSharePoint && spPhase === "sites" && renderSitesList()}
                {isSharePoint && spPhase === "libraries" && renderDrivesList()}
                {showingFiles && (!isSharePoint || spPhase === "files") && renderFileList()}

                {/* Selection info and actions */}
                <Box
                    sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                    <Typography variant="body2" color="text.secondary">
                        {selectedItem ? `Selected: ${selectedItem.name}` : emptyStateHint}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button variant="text" onClick={handleCancel}>
                            Cancel
                        </Button>
                        {showingFiles && selectedItem?.folder && (
                            <Button
                                variant="outlined"
                                onClick={() => navigateToFolder(selectedItem)}
                                sx={{
                                    borderColor: "#0078d4",
                                    color: "#0078d4",
                                    "&:hover": {
                                        borderColor: "#106ebe",
                                        backgroundColor: "rgba(0, 120, 212, 0.04)"
                                    }
                                }}
                            >
                                Open Folder
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            onClick={handleSelect}
                            disabled={!selectedItem || !showingFiles}
                            sx={{
                                backgroundColor: "#0078d4",
                                "&:hover": { backgroundColor: "#106ebe" }
                            }}
                        >
                            Select
                        </Button>
                    </Box>
                </Box>
            </Box>
        );
    };

    const modeLabel = isSharePoint ? "SharePoint" : "OneDrive";

    // Render based on status
    const renderContent = () => {
        switch (status) {
            case "initializing":
                return (
                    <Box className="onedrive-picker-page__loading">
                        <CircularProgress size={40} />
                        <Typography variant="body1">Initializing...</Typography>
                    </Box>
                );

            case "invalid-origin":
                return (
                    <Box className="onedrive-picker-page__error">
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {errorMessage}
                        </Alert>
                        <Typography variant="body2" color="text.secondary">
                            Please close this window and try again from the application.
                        </Typography>
                    </Box>
                );

            case "error":
                return (
                    <Box className="onedrive-picker-page__error">
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {errorMessage}
                        </Alert>
                        <Box className="onedrive-picker-page__actions">
                            <Button variant="contained" onClick={handleRetry}>
                                Try Again
                            </Button>
                            <Button variant="text" onClick={handleCancel} sx={{ mt: 1 }}>
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                );

            case "ready":
                return (
                    <Box className="onedrive-picker-page__ready">
                        {isSharePoint ? (
                            <BusinessIcon sx={{ fontSize: 64, color: "#0078d4", mb: 2 }} />
                        ) : (
                            <FolderIcon sx={{ fontSize: 64, color: "#0078d4", mb: 2 }} />
                        )}
                        <Typography variant="h6" gutterBottom>
                            Select {modeLabel} File or Folder
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Click the button below to sign in and select a file or folder from{" "}
                            {modeLabel}.
                        </Typography>
                        <Box className="onedrive-picker-page__actions">
                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleOpenPicker}
                                sx={{
                                    backgroundColor: "#0078d4",
                                    "&:hover": { backgroundColor: "#106ebe" }
                                }}
                                startIcon={
                                    <img
                                        src="https://docs.microsoft.com/en-us/azure/active-directory/develop/media/howto-add-branding-in-azure-ad-apps/ms-symbollockup_mssymbol_19.svg"
                                        alt="Microsoft"
                                        style={{ width: 20, height: 20 }}
                                        onError={e => {
                                            (e.target as HTMLImageElement).style.display = "none";
                                        }}
                                    />
                                }
                            >
                                Open {modeLabel}
                            </Button>
                            <Button variant="text" onClick={handleCancel} sx={{ mt: 1 }}>
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                );

            case "authenticating":
                return (
                    <Box className="onedrive-picker-page__loading">
                        <CircularProgress size={40} />
                        <Typography variant="body1">Redirecting to Microsoft login...</Typography>
                    </Box>
                );

            case "picking":
                return (
                    <Box className="onedrive-picker-page__picking">
                        <Typography variant="h6" gutterBottom>
                            {isSharePoint
                                ? "Select a file or folder from SharePoint"
                                : "Select a file or folder from OneDrive"}
                        </Typography>
                        {renderFilePicker()}
                    </Box>
                );

            case "success":
                return (
                    <Box className="onedrive-picker-page__loading">
                        <CircularProgress size={40} />
                        <Typography variant="body1">Selection complete. Closing...</Typography>
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <div className="onedrive-picker-page">
            <div className="onedrive-picker-page__container">{renderContent()}</div>
        </div>
    );
};

export default OneDrivePickerPage;
